import { useState } from 'react';
import styled from 'styled-components';
import { Button, Input, Modal } from 'antd';

/**
 * The assistant's question in the count panel while "Look again" or "Tell it what it missed" is the chosen tool
 * (design: corrections_design.html, section 4): one button with its price, the answer in one line, and the
 * whole-video check behind a link. Its proposals are reviewed in the panel's list below.
 */

const tk = ({ theme: t }) => t.token;

const Card = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => tk(p).marginXS}px;
`;

const Head = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: baseline;
	gap: ${p => tk(p).marginXS}px;
`;

const Label = styled.span`
	font-size: ${p => tk(p).fontSizeXXS}px;
	font-weight: ${p => tk(p).fontWeightStrong};
	letter-spacing: 0.09em;
	text-transform: uppercase;
	color: ${p => tk(p).colorTextTertiary};
`;

const Small = styled.span`
	font-size: ${p => tk(p).fontSizeXS}px;
	color: ${p => tk(p).colorTextTertiary};
	line-height: ${p => tk(p).lineHeightSM};
`;

const Said = styled.div`
	font-size: ${p => tk(p).fontSizeXS}px;
	color: ${p => tk(p).colorText};
`;

const Price = styled.span`
	font-weight: ${p => tk(p).fontWeightStrong};
	opacity: 0.8;
`;

const Link = styled.button`
	align-self: flex-start;
	border: 0;
	padding: 0;
	background: transparent;
	color: ${p => tk(p).colorBrandLight};
	font-size: ${p => tk(p).fontSizeXS}px;
	text-decoration: underline;
	cursor: pointer;
	&:disabled {
		opacity: 0.5;
		cursor: default;
	}
`;

const credits = n => (n == null ? '' : `${n} credit${n === 1 ? '' : 's'}`);

const errorText = err =>
	err?.response?.status === 402
		? 'Not enough credits'
		: err?.response?.data?.detail ||
			'The assistant could not look at this frame. You were not charged.';

const AssistantCard = ({
	tool,
	subject,
	at,
	price,
	busy,
	onLookAgain,
	onHint,
	check = null,
	onPlanCheck = null,
	onStartCheck = null,
	// the answer of a Look again ({ kept, at }), for the panel's answer block
	onAnswer = null,
}) => {
	const [said, setSaid] = useState('');
	const [text, setText] = useState('');
	// the hook's modal renders inside the app's ConfigProvider, so it takes the dark theme (Modal.confirm does not)
	const [modal, modalHolder] = Modal.useModal();
	const run = async (kind, action, ...args) => {
		setSaid('');
		try {
			const out = await action(...args);
			const misses = (out?.proposals || []).filter(p => p.type === 'add').length;
			if (kind === 'again') onAnswer?.({ kept: out?.kept ?? null, at });
			setSaid(
				kind === 'tell'
					? misses
						? 'Found it · accept it below'
						: `Could not find it · try saying where it is`
					: `Keeps ${out.kept} · ${out.rejected} to take out · ${misses} missed · review below`
			);
		} catch (err) {
			setSaid(errorText(err));
		}
	};
	const checking = check?.status === 'running';
	const askCheck = async () => {
		setSaid('');
		try {
			const plan = (await onPlanCheck())?.data;
			if (!plan?.frames) {
				setSaid('Nothing to check yet');
				return;
			}
			modal.confirm({
				title: 'Check the whole video?',
				content: `The assistant looks at ${plan.frames} frames, so that it sees every counted ${subject} once. Its proposals appear in the panel as it goes. Price: about ${credits(plan.credits)} (you have ${plan.balance}).`,
				okText: 'Check the whole video',
				onOk: async () => {
					try {
						await onStartCheck();
					} catch (err) {
						setSaid(errorText(err));
					}
				},
			});
		} catch (err) {
			setSaid(errorText(err));
		}
	};
	const checkLine = checking
		? `Checking the whole video · ${check.done} of ${check.total} frames`
		: check?.status === 'stopped'
			? `The check stopped after ${check.done} frames: out of credits`
			: null;

	return (
		<Card>
			{modalHolder}
			<Head>
				<Label>Ask the assistant</Label>
				{at && <Small>at {at}</Small>}
			</Head>
			{tool === 'tell' ? (
				<>
					<Small>
						What do you see that has no dot? Say where it is and what it looks like.
					</Small>
					<Input.TextArea
						rows={2}
						maxLength={500}
						value={text}
						placeholder={`e.g. "the ${subject} behind the fence"`}
						onChange={e => setText(e.target.value)}
					/>
					<Button
						type="primary"
						block
						loading={busy}
						disabled={!text.trim()}
						onClick={() => run('tell', onHint, text.trim())}>
						Find it {price?.hint != null && <Price>· {credits(price.hint)}</Price>}
					</Button>
				</>
			) : (
				<>
					<Button
						type="primary"
						block
						loading={busy}
						onClick={() => run('again', onLookAgain)}>
						Look again at this frame{' '}
						{price?.look_again != null && <Price>· {credits(price.look_again)}</Price>}
					</Button>
					<Small>
						It checks every dot in view and looks for any {subject} without one. You
						decide what to apply.
					</Small>
				</>
			)}
			{(checkLine || said) && <Said>{checkLine || said}</Said>}
			{onPlanCheck && (
				<Link type="button" disabled={busy || checking} onClick={askCheck}>
					Check the whole video…
				</Link>
			)}
		</Card>
	);
};

export default AssistantCard;
